import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCw, Download, ChevronDown, ChevronRight, Settings,
  Users, Save, ExternalLink, X, TrendingUp, FileText,
} from 'lucide-react';

import ProspectAiAnalysis from './ProspectAiAnalysis';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  nouvelle_demande: 'Nouvelle demande',
  a_analyser: 'À analyser',
  contact_a_effectuer: 'Contact à effectuer',
  rdv_programme: 'RDV programmé',
  proposition_envoyee: 'Proposition envoyée',
  client: 'Client',
  non_conclu: 'Non conclu',
  a_relancer: 'À relancer',
};

const STATUS_COLOR = {
  nouvelle_demande: 'bg-blue-500/20 text-blue-300 border-blue-500/50',
  a_analyser: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50',
  contact_a_effectuer: 'bg-orange-500/20 text-orange-300 border-orange-500/50',
  rdv_programme: 'bg-purple-500/20 text-purple-300 border-purple-500/50',
  proposition_envoyee: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/50',
  client: 'bg-green-500/20 text-green-300 border-green-500/50',
  non_conclu: 'bg-gray-500/20 text-gray-300 border-gray-500/50',
  a_relancer: 'bg-pink-500/20 text-pink-300 border-pink-500/50',
};

const csvEscape = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v).replace(/"/g, '""');
  return /[",\n;]/.test(s) ? `"${s}"` : s;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));
  } catch { return iso; }
};

const ProspectsAdmin = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [configOpen, setConfigOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [configSaving, setConfigSaving] = useState(false);

  const fetchProspects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.set('status', filter);
      if (search) params.set('q', search);
      const { data: d } = await axios.get(`${API_URL}/api/admin/prospects?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(d);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [token, filter, search]);

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/annonce-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConfig(data);
    } catch { /* noop */ }
  }, [token]);

  useEffect(() => { fetchProspects(); }, [fetchProspects]);
  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const stats = data?.stats || {};
  const prospects = data?.prospects || [];

  const filtered = useMemo(() => prospects, [prospects]);

  const exportCsv = () => {
    const headers = ['Date', 'Nom', 'Email', 'Téléphone', 'Statut', 'Lien annonce', 'Type', 'Localisation', 'Prix affiché', 'Budget max', 'Usage', 'Expérience', 'Urgence', 'Source', 'UTM Campaign'];
    const rows = filtered.map((p) => [
      csvEscape(formatDate(p.created_at)),
      csvEscape(`${p.prenom || ''} ${p.nom || ''}`.trim()),
      csvEscape(p.email),
      csvEscape(p.telephone),
      csvEscape(STATUS_LABELS[p.status] || p.status),
      csvEscape(p.lien_annonce),
      csvEscape(p.type_bateau),
      csvEscape(p.localisation),
      csvEscape(p.prix_affiche),
      csvEscape(p.budget_max),
      csvEscape(p.usage),
      csvEscape(p.experience),
      csvEscape(p.urgence),
      csvEscape(p.utm_source || 'direct'),
      csvEscape(p.utm_campaign || ''),
    ]);
    const csv = [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prospects-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const updateProspect = async (id, updates) => {
    try {
      await axios.patch(`${API_URL}/api/admin/prospects/${id}`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchProspects();
    } catch (err) {
      alert(`Erreur : ${err?.response?.data?.detail || err.message}`);
    }
  };

  const saveConfig = async (updates) => {
    setConfigSaving(true);
    try {
      const { data } = await axios.patch(`${API_URL}/api/admin/annonce-config`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConfig(data);
    } catch (err) {
      alert(`Erreur config : ${err?.response?.data?.detail || err.message}`);
    } finally {
      setConfigSaving(false);
    }
  };

  return (
    <div className="bg-[#302f2c] border border-[#3f4816] rounded-xl overflow-hidden mb-6" data-testid="prospects-admin">
      <div className="p-4 border-b border-[#3f4816] flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Users className="text-[#d9fb06]" size={20} />
          <h2 className="text-lg font-bold text-[#dfddd6]">
            Prospects <span className="text-[#888680]">({stats.total || 0})</span>
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setConfigOpen(true)}
            className="flex items-center gap-2 text-xs border border-[#3f4816] text-[#dfddd6] px-3 py-2 rounded-lg hover:border-[#d9fb06] hover:text-[#d9fb06]"
            data-testid="prospects-config-btn"
          >
            <Settings size={14} /> Config offre
          </button>
          <button
            onClick={fetchProspects}
            className="flex items-center gap-2 text-xs border border-[#3f4816] text-[#dfddd6] px-3 py-2 rounded-lg hover:border-[#d9fb06] hover:text-[#d9fb06]"
            data-testid="prospects-refresh-btn"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualiser
          </button>
          <button
            onClick={exportCsv}
            disabled={!filtered.length}
            className="flex items-center gap-2 text-xs bg-[#d9fb06] text-[#1a1c1b] font-bold px-3 py-2 rounded-lg disabled:opacity-50"
            data-testid="prospects-export-btn"
          >
            <Download size={14} /> CSV
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 border-b border-[#3f4816]">
        <StatCard label="Total" value={stats.total || 0} />
        <StatCard label="Nouveaux" value={stats.nouvelle_demande || 0} />
        <StatCard label="Contacter" value={stats.contact_a_effectuer || 0} />
        <StatCard label="RDV" value={stats.rdv_programme || 0} />
        <StatCard label="Clients" value={stats.client || 0} highlight icon={<TrendingUp size={14} />} />
        <StatCard label="Taux conv." value={`${(stats.conversion_rate_pct || 0).toFixed(1)}%`} highlight />
      </div>

      {/* Filters */}
      <div className="p-4 border-b border-[#3f4816] flex flex-wrap gap-2 items-center">
        <input
          type="search"
          placeholder="Rechercher (nom, email, URL)…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-3 py-1.5 text-sm text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none max-w-xs"
          data-testid="prospects-search"
        />
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase ${filter === 'all' ? 'bg-[#d9fb06] text-[#1a1c1b]' : 'bg-[#1a1c1b] border border-[#3f4816] text-[#888680] hover:text-[#d9fb06]'}`}
        >Tous</button>
        {Object.entries(STATUS_LABELS).map(([k, l]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold ${filter === k ? 'bg-[#d9fb06] text-[#1a1c1b]' : 'bg-[#1a1c1b] border border-[#3f4816] text-[#888680] hover:text-[#d9fb06]'}`}
            data-testid={`prospects-filter-${k}`}
          >
            {l} ({stats[k] || 0})
          </button>
        ))}
      </div>

      <div className="max-h-[700px] overflow-y-auto">
        {loading && <div className="p-8 text-center text-[#888680]"><Loader2 className="animate-spin inline mr-2" size={18} /> Chargement…</div>}
        {error && <div className="p-6 text-red-400 text-sm">{error}</div>}
        {!loading && !error && filtered.length === 0 && (
          <div className="p-8 text-center text-[#888680]" data-testid="prospects-empty">Aucun prospect pour ce filtre.</div>
        )}
        {!loading && !error && filtered.map((p) => {
          const isOpen = expandedId === p.id;
          return (
            <div key={p.id} className="border-b border-[#3f4816]" data-testid={`prospect-row-${p.id}`}>
              <button
                onClick={() => setExpandedId(isOpen ? null : p.id)}
                className="w-full p-4 flex flex-wrap items-center justify-between gap-3 hover:bg-[#1a1c1b]/40 text-left"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {isOpen ? <ChevronDown size={16} className="text-[#d9fb06]" /> : <ChevronRight size={16} className="text-[#888680]" />}
                  <div className="min-w-0">
                    <p className="text-[#dfddd6] font-semibold truncate">{p.prenom} {p.nom}</p>
                    <p className="text-xs text-[#888680] truncate">{p.email} · {formatDate(p.created_at)}</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full border ${STATUS_COLOR[p.status] || 'bg-gray-500/20 text-gray-300 border-gray-500/50'}`}>
                  {STATUS_LABELS[p.status] || p.status}
                </span>
              </button>
              {isOpen && <ProspectDetail p={p} onUpdate={updateProspect} onRefresh={fetchProspects} token={token} />}
            </div>
          );
        })}
      </div>

      {configOpen && config && (
        <ConfigModal
          config={config}
          onSave={saveConfig}
          onClose={() => setConfigOpen(false)}
          saving={configSaving}
        />
      )}
    </div>
  );
};

const StatCard = ({ label, value, highlight, icon }) => (
  <div className="bg-[#1a1c1b] border border-[#3f4816] rounded-lg p-3">
    <p className="text-xs text-[#888680] uppercase font-bold tracking-wide">{label}</p>
    <p className={`mt-1 text-lg font-black flex items-center gap-1 ${highlight ? 'text-[#d9fb06]' : 'text-[#dfddd6]'}`}>
      {icon} {value}
    </p>
  </div>
);

const ProspectDetail = ({ p, onUpdate, onRefresh, token }) => {
  const [note, setNote] = useState(p.admin_note || '');
  const [followup, setFollowup] = useState(p.next_followup || '');
  const [status, setStatus] = useState(p.status);
  const [value, setValue] = useState((p.estimated_value_cents || 0) / 100);
  const [saving, setSaving] = useState(false);

  const pdfUrl = `${API_URL}/api/admin/prospects/${p.id}/pdf?token=${encodeURIComponent(token || '')}`;

  const save = async () => {
    setSaving(true);
    await onUpdate(p.id, {
      status,
      admin_note: note,
      next_followup: followup || null,
      estimated_value_cents: Math.round(Number(value) * 100) || 0,
    });
    setSaving(false);
  };

  return (
    <div className="p-4 pt-0 pl-10 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
      <div className="space-y-2">
        <Row label="Téléphone" v={p.telephone} />
        <Row label="Contact préféré" v={p.preference_contact} />
        <Row label="Lien annonce" v={<a href={p.lien_annonce} target="_blank" rel="noopener noreferrer" className="text-[#d9fb06] underline inline-flex items-center gap-1 break-all">{p.lien_annonce} <ExternalLink size={11} /></a>} />
        <Row label="Type / Prix / Loc" v={`${p.type_bateau} · ${p.prix_affiche} · ${p.localisation}`} />
        <Row label="Budget max" v={p.budget_max} />
        <Row label="Usage / Xp / Urgence" v={`${p.usage} · ${p.experience} · ${p.urgence}`} />
        <Row label="Question" v={<span className="whitespace-pre-wrap">{p.question_principale}</span>} />
        <Row label="Source" v={`${p.utm_source || 'direct'}${p.utm_campaign ? ` · ${p.utm_campaign}` : ''}`} />
        <Row label="Documents" v={p.documents?.length ? `${p.documents.length} fichier(s)` : 'aucun'} />
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-[#888680] uppercase font-bold block mb-1">Statut</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid={`prospect-status-select-${p.id}`}
            className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded px-3 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none">
            {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-[#888680] uppercase font-bold block mb-1">Valeur potentielle (€)</label>
          <input type="number" min="0" value={value} onChange={(e) => setValue(e.target.value)}
            className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded px-3 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none" />
        </div>
        <div>
          <label className="text-xs text-[#888680] uppercase font-bold block mb-1">Prochain rappel</label>
          <input type="date" value={followup} onChange={(e) => setFollowup(e.target.value)}
            className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded px-3 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none" />
        </div>
        <div>
          <label className="text-xs text-[#888680] uppercase font-bold block mb-1">Note interne</label>
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={5000}
            className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded px-3 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none resize-y" />
        </div>
        <button onClick={save} disabled={saving}
          data-testid={`prospect-save-btn-${p.id}`}
          className="w-full bg-[#d9fb06] text-[#1a1c1b] font-bold uppercase text-sm rounded-full py-2 hover:bg-[#d9fb06]/90 disabled:opacity-50 flex items-center justify-center gap-2">
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Enregistrer
        </button>
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`prospect-pdf-btn-${p.id}`}
          className="w-full bg-[#1a1c1b] border border-[#d9fb06] text-[#d9fb06] font-bold uppercase text-sm rounded-full py-2 hover:bg-[#d9fb06] hover:text-[#1a1c1b] transition-colors flex items-center justify-center gap-2"
        >
          <FileText size={14} /> Voir le PDF client
        </a>
      </div>
      <ProspectAiAnalysis prospect={p} token={token} onUpdated={onRefresh} />
    </div>
  );
};

const Row = ({ label, v }) => (
  <div className="flex items-baseline gap-2">
    <span className="text-[#888680] text-xs uppercase min-w-[110px] flex-shrink-0">{label}</span>
    <span className="text-[#dfddd6] flex-1">{v}</span>
  </div>
);

const ConfigModal = ({ config, onSave, onClose, saving }) => {
  const [mode, setMode] = useState(config.mode);
  const [price, setPrice] = useState((config.price_cents / 100).toFixed(2));
  const [freeTotal, setFreeTotal] = useState(config.free_total);
  const [delai, setDelai] = useState(config.delai_reponse_heures);
  const [active, setActive] = useState(config.active);
  const [calendlyUrl, setCalendlyUrl] = useState(config.calendly_url || '');

  const submit = (e) => {
    e.preventDefault();
    onSave({
      mode, active,
      price_cents: Math.round(Number(price) * 100) || 0,
      free_total: Number(freeTotal) || 0,
      delai_reponse_heures: Number(delai) || 48,
      calendly_url: calendlyUrl.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <form onSubmit={submit} className="bg-[#1a1c1b] border-2 border-[#d9fb06] rounded-2xl max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="config-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-black text-[#d9fb06] uppercase">Config Analyse d&apos;annonce</h3>
          <button type="button" onClick={onClose} className="text-[#888680] hover:text-[#d9fb06]"><X size={20} /></button>
        </div>
        <div className="space-y-4 text-sm">
          <div>
            <label className="text-xs text-[#888680] uppercase font-bold block mb-1">Mode</label>
            <div className="flex gap-2">
              {['free', 'paid'].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  data-testid={`config-mode-${m}`}
                  className={`flex-1 py-2 rounded-full font-bold uppercase text-xs ${mode === m ? 'bg-[#d9fb06] text-[#1a1c1b]' : 'bg-[#302f2c] border border-[#3f4816] text-[#888680]'}`}
                >
                  {m === 'free' ? 'Gratuit (lancement)' : 'Payant'}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#888680] uppercase font-bold block mb-1">Prix (€)</label>
              <input type="number" min="0" value={price} onChange={(e) => setPrice(e.target.value)}
                data-testid="config-price-input"
                className="w-full bg-[#302f2c] border border-[#3f4816] rounded px-3 py-2 text-[#dfddd6]" />
            </div>
            <div>
              <label className="text-xs text-[#888680] uppercase font-bold block mb-1">Analyses gratuites totales</label>
              <input type="number" min="0" value={freeTotal} onChange={(e) => setFreeTotal(e.target.value)}
                data-testid="config-free-total-input"
                className="w-full bg-[#302f2c] border border-[#3f4816] rounded px-3 py-2 text-[#dfddd6]" />
              <p className="text-xs text-[#888680] mt-1">Utilisées : {config.free_used}</p>
            </div>
          </div>
          <div>
            <label className="text-xs text-[#888680] uppercase font-bold block mb-1">Délai de réponse (heures)</label>
            <input type="number" min="1" value={delai} onChange={(e) => setDelai(e.target.value)}
              className="w-full bg-[#302f2c] border border-[#3f4816] rounded px-3 py-2 text-[#dfddd6]" />
          </div>
          <div>
            <label className="text-xs text-[#888680] uppercase font-bold block mb-1">Lien Calendly (RDV 20 min)</label>
            <input type="url" value={calendlyUrl} onChange={(e) => setCalendlyUrl(e.target.value)}
              data-testid="config-calendly-input"
              placeholder="https://calendly.com/votre-compte/appel-decouverte"
              className="w-full bg-[#302f2c] border border-[#3f4816] rounded px-3 py-2 text-[#dfddd6]" />
            <p className="text-xs text-[#888680] mt-1">
              {calendlyUrl ? "Un bouton \"Réserver un appel\" apparaîtra après chaque demande d'analyse" : "Laissez vide pour désactiver le bouton RDV"}
            </p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="accent-[#d9fb06]" />
            <span className="text-[#dfddd6]">Offre active (visible sur le site)</span>
          </label>
        </div>
        <div className="flex gap-2 justify-end mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-[#3f4816] text-[#dfddd6] hover:border-[#d9fb06] hover:text-[#d9fb06] text-sm font-bold uppercase">Annuler</button>
          <button type="submit" disabled={saving} data-testid="config-save-btn" className="px-6 py-2 rounded-full bg-[#d9fb06] text-[#1a1c1b] font-bold uppercase text-sm disabled:opacity-50">
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProspectsAdmin;
